import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
// eslint-disable-next-line import/extensions
import { connect } from 'redux-zero/react';
import { Helmet } from 'react-helmet';
import { Field, Formik } from 'formik';
import { FormattedMessage, injectIntl } from 'react-intl';
import cn from 'classnames';
import Carousel from 'nuka-carousel';

import { withLocaleContext } from '../../locales/locale-context.js';
import { actions } from '../../shared/store.js';
import { validateEmail } from '../../shared/utils.js';
import EcosystemService from './ecosystem.service.js';

import { FormTextBox } from '../../components/ui-toolkit/form-inputs.js';
import { PrimaryButton } from '../../components/ui-toolkit/buttons.js';

import PageHeader from '../../components/page-header/page-header.component.js';
import NotImplemented from '../../components/not-implemented/not-implemented.js';

import research from '../../assets/images/ecosystem/img_research.png';
import startUps from '../../assets/images/ecosystem/img_startups.png';
import industry from '../../assets/images/ecosystem/img_industry.png';
import social from '../../assets/images/ecosystem/img_social.png';

import './ecosystem.scss';

const ListItem = ({
	item, hoverItem, mouseLeave, openGallery,
}) => (
	<div className="item" onMouseOver={hoverItem} onMouseLeave={mouseLeave}>
		<span className="label">{item.label}</span>
		{item.link && !item.externalLink && (
			<span className="right">
				<Link to={item.link}>
					<FormattedMessage id="ecosystem.know_more" />
				</Link>
			</span>
		)}
		{item.link && item.externalLink && (
			<span className="right">
				<a href={item.link} target="_blank" rel="noopener noreferrer">
					<FormattedMessage id="ecosystem.know_more" />
				</a>
			</span>
		)}
		{item.gallery && item.galleryId && (
			<span className="right" onClick={openGallery}>
				<FormattedMessage id="ecosystem.open_gallery" />
			</span>
		)}
	</div>
);

class List extends React.Component {
	state = {
		itemHover: null,
	};

	hoverItem = (itemHover) => {
		this.setState({ itemHover });
	};

	render() {
		const { list, handleOpenGallery } = this.props;

		const { itemHover } = this.state;

		return (
			<div className="list-wrapper">
				<div className="row">
					<div className="col-xs-12 col-md-5">
						<div className="list">
							{list
								&& list.map((item, index) => (
									<ListItem
										item={item}
										hoverItem={() => this.hoverItem(index)}
										mouseLeave={() => this.hoverItem(null)}
										openGallery={() => handleOpenGallery(item.galleryId)}
									/>
								))}
						</div>
					</div>
				</div>
				<div className="hover-image">
					<div className="hover-image-wrapper">
						{list
							&& list.map((item, index) => (
								<div className={`image ${cn({ active: itemHover === index })}`}>
									<img src={item.image} alt="gallery" />
								</div>
							))}
					</div>
				</div>
			</div>
		);
	}
}

// eslint-disable-next-line react/no-multi-comp
class EcosystemPage extends React.Component {
	static prefetchData = async language => EcosystemService.getPageData(language);

	state = {
		galleryOpen: false,
		galleryToDisplay: null,
		hasError: false,
		hasFormError: false,
		submitted: false,
	};

	componentDidMount() {
		const { ecosystemPageData, setLoading } = this.props;

		if (!ecosystemPageData) {
			this.fetchData();
		} else {
			setLoading(false);
		}
	}

	componentDidUpdate(prevProps) {
		const {
			localeContext: { locale },
		} = this.props;

		if (prevProps.localeContext.locale.value !== locale.value) {
			this.fetchData();
		}
	}

	componentWillUnmount() {
		clearTimeout(this.sliderTimer);
	}

	toggleGallery = () => {
		this.setState(prevState => ({ galleryOpen: !prevState.galleryOpen }));
	};

	openGallery = (galleryId) => {
		const { ecosystemGalleries } = this.props;
		if (ecosystemGalleries === null || !(galleryId in ecosystemGalleries)) {
			this.fetchGallery(galleryId);
		} else if (galleryId in ecosystemGalleries) {
			this.toggleGallery();
		}
		this.setState({ galleryToDisplay: galleryId });
	};

	fetchData() {
		const {
			setLoading,
			setEcosystemPageData,
			localeContext: { locale },
		} = this.props;

		setLoading(true);

		EcosystemPage.prefetchData(locale.value)
			.then((res) => {
				setEcosystemPageData(res);
				setLoading(false);
			})
			.catch(() => {
				setLoading(false);
				this.setState({
					hasError: true,
				});
			});
	}

	fetchGallery(galleryId) {
		const { setEcosystemGalleries } = this.props;
		EcosystemService.getGallery(galleryId)
			.then((galleries) => {
				setEcosystemGalleries(galleryId, galleries.data.entries);
				this.toggleGallery();
			})
			.catch(() => {
				this.setState({
					hasError: true,
				});
			});
	}

	render() {
		const {
			hasError,
			submitted,
			hasFormError,
			galleryOpen,
			galleryToDisplay,
		} = this.state;

		const {
			isLoading,
			intl,
			ecosystemPageData,
			ecosystemGalleries,
		} = this.props;

		if (isLoading) return null;
		if (hasError || !ecosystemPageData) {
			return <NotImplemented />;
		}

		const { title } = ecosystemPageData;

		const list = [
			{
				label: intl.formatMessage({ id: 'ecosystem.research' }),
				gallery: 'research',
				galleryId: 'ecosystemResearchGallery',
				image: research,
			},
			{
				label: intl.formatMessage({ id: 'ecosystem.start_ups' }),
				link: '/companies',
				image: startUps,
			},
			{
				label: intl.formatMessage({ id: 'ecosystem.industry' }),
				gallery: 'industry',
				galleryId: 'ecosystemIndustryGallery',
				image: industry,
			},
			{
				label: intl.formatMessage({ id: 'ecosystem.social_responsibilities' }),
				link: 'http://www.centrocienciajunior.com/',
				externalLink: true,
				image: social,
			},
		];

		return (
			<Fragment>
				<div id="ecosystem-page">
					<Helmet>
						<title>
							{intl.formatMessage({
								id: 'navigation.ecosystem',
							})}
						</title>
					</Helmet>
					<section className="header">
						<div className="container container-fluid">
							<div className="row">
								<div className="col-xs-12 col-md-6">
									<div className="container">
										<div className="row title ">
											<div className="col-xs-12 ">
												<PageHeader
													title={<FormattedMessage id="ecosystem.title" />}
												/>
											</div>
											<div className="col-xs-12">
												<h2>{title}</h2>
											</div>
										</div>
									</div>
								</div>
								<div className="col-xs-12 col-md-6 image" />
							</div>
						</div>
					</section>
					<section className="galleries">
						<div className="container container-fluid">
							<List list={list} handleOpenGallery={this.openGallery} />
						</div>
					</section>
					<section className="notifications">
						<div className="container container-fluid">
							<div className="row">
								<div className="col-xs-12 col-md-5 title">
									<p>
										<FormattedMessage id="ecosystem.notification.first_line" />
										<span className="bold">
											<FormattedMessage id="ecosystem.notification.last_line" />
										</span>
									</p>
								</div>
								<div className="col-xs-12 col-md-7 input-section center-xs middle-xs">
									<Formik
										enableReinitialize
										initialValues={{
											email: '',
										}}
										onSubmit={(values, { setSubmitting, resetForm }) => {
											setSubmitting(true);
											this.setState({ hasFormError: false });
											EcosystemService.submitEmail(values)
												.then(() => {
													setSubmitting(false);
													this.setState({
														submitted: true,
														hasFormError: false,
													});
												})
												.catch(() => {
													setSubmitting(false);
													this.setState({
														hasFormError: true,
													});
												});
										}}
									>
										{({
											errors,
											dirty,
											touched,
											handleSubmit,
											isSubmitting,
											isValid,
										}) => (
											<form onSubmit={handleSubmit} noValidate>
												<div className="row center-xs">
													<div
														className={`input-container ${cn({
															submitted,
														})} ${cn({ hasFormError })}`}
													>
														<Field
															component={FormTextBox}
															type="email"
															name="email"
															placeholder={intl.formatMessage({
																id: 'ecosystem.subscribe.placeholder',
															})}
															fullWidth
															error={errors.email && touched.email}
															validate={(value) => {
																let error = false;
																if (!validateEmail(value)) {
																	error = intl.formatMessage({
																		id: 'errors.invalid_email',
																	});
																}
																if (!value) {
																	error = '';
																}
																return error;
															}}
															validationMessage={errors.email}
															required
														/>
														<PrimaryButton
															type="submit"
															disabled={
																(!isValid || !dirty || isSubmitting)
																&& !hasFormError
															}
														>
															{hasFormError ? (
																<FormattedMessage id="ecosystem.retry" />
															) : (
																<FormattedMessage id="ecosystem.subscribe" />
															)}
														</PrimaryButton>
														<div className="success-message">
															<FormattedMessage id="ecosystem.subscribe.thanks" />
														</div>
														<div className="error-message">
															<FormattedMessage id="ecosystem.subscribe.errorForm" />
														</div>
													</div>
												</div>
											</form>
										)}
									</Formik>
								</div>
							</div>
						</div>
					</section>
				</div>
				<div className={`gallery-player ${cn({ active: galleryOpen })} `}>
					<div className="gallery-wrapper">
						<div className="close" onClick={this.toggleGallery} />
						<div className="gallery">
							<Carousel
								wrapAround
								autoplay
								beforeSlide={this.handleBeforeSlide}
								renderCenterLeftControls={({ previousSlide }) => {
									if (
										ecosystemGalleries === null
										|| !(galleryToDisplay in ecosystemGalleries)
										|| ecosystemGalleries[galleryToDisplay].length <= 1
									) return null;
									return (
										<button className="left-arrow" onClick={previousSlide} />
									);
								}}
								renderCenterRightControls={({ nextSlide }) => {
									if (
										ecosystemGalleries === null
										|| !(galleryToDisplay in ecosystemGalleries)
										|| ecosystemGalleries[galleryToDisplay].length <= 1
									) return null;
									return <button className="right-arrow" onClick={nextSlide} />;
								}}
							>
								{ecosystemGalleries !== null
									&& galleryToDisplay in ecosystemGalleries
									&& ecosystemGalleries[galleryToDisplay].map(photo => (
										<img
											src={`${process.env.REACT_APP_API_ENDPOINT
												+ photo.image.path}`}
											alt=""
										/>
									))}
							</Carousel>
						</div>
					</div>
				</div>
			</Fragment>
		);
	}
}

const mapToProps = ({ ecosystemPageData, ecosystemGalleries }) => ({
	ecosystemPageData,
	ecosystemGalleries,
});

export default withLocaleContext(
	injectIntl(
		connect(
			mapToProps,
			actions,
		)(EcosystemPage),
	),
);

export { EcosystemPage };
